import React, { createContext, FC, ReactNode, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useApolloClient, useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { dataSetsTemplate, dataSetSwitchStatusTemplate, switchDataSetTemplate } from 'apollo/templates/data-set-switch';
import { getDenthubSettingsTemplate } from 'apollo/templates/denthub-settings';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import { DataSet, DataSetSummary, DataSetSwitchStatus } from 'models/data-set-switch';
import { DenthubSetting } from 'models/denthub-settings';
import { formatDate } from 'utils/format';
import { useAppSettings } from './app-settings';

export const DB_SWITCH_ENABLED = process.env.REACT_APP_ENABLE_DB_SWITCH === 'true';

// While a switch runs the status is polled often enough to feel live; when
// idle, slowly, so a switch another super admin starts still shows up.
const RUNNING_POLL_MS = 3_000;
const IDLE_POLL_MS = 30_000;

type DataSetSwitchContextValue = {
    status: DataSetSwitchStatus | null;
    dataSets: DataSetSummary[];
    isRunning: boolean;
    starting: boolean;
    startSwitch: (dataSet: DataSet) => void;
};

const DataSetSwitchContext = createContext<DataSetSwitchContextValue>({
    status: null,
    dataSets: [],
    isRunning: false,
    starting: false,
    startSwitch: () => undefined,
});

/** Percentage of documents copied, or undefined while the step has no measurable progress. */
export const switchProgress = (status: DataSetSwitchStatus): number | undefined => {
    if (status.step !== 'COPYING' || !status.totalDocuments) return undefined;
    return Math.min(100, Math.round((status.copiedDocuments / status.totalDocuments) * 100));
};

export const DataSetSwitchProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { t: translate } = useTranslation(['app-settings']);
    const client = useApolloClient();
    const { setAppSettings } = useAppSettings();
    const { setAppToasterContent } = useContext(AppToasterContext);

    const { data: statusData, refetch: refetchStatus } = useQuery<{ dataSetSwitchStatus: DataSetSwitchStatus | null }>(
        dataSetSwitchStatusTemplate,
        { skip: !DB_SWITCH_ENABLED, fetchPolicy: 'network-only', nextFetchPolicy: 'network-only' },
    );
    const { data: dataSetsData, refetch: refetchDataSets } = useQuery<{ dataSets: DataSetSummary[] }>(
        dataSetsTemplate,
        { skip: !DB_SWITCH_ENABLED },
    );

    const status = statusData?.dataSetSwitchStatus ?? null;
    const isRunning = status?.state === 'RUNNING';

    // Polling with useQuery's pollInterval does not reliably pick up interval
    // changes, so the timer is driven here.
    useEffect(() => {
        if (!DB_SWITCH_ENABLED) return undefined;
        const timer = setInterval(
            () => {
                refetchStatus().catch(() => undefined);
            },
            isRunning ? RUNNING_POLL_MS : IDLE_POLL_MS,
        );
        return () => clearInterval(timer);
    }, [isRunning, refetchStatus]);

    const dataSetLabel = useCallback(
        (dataSet: DataSet, snapshotDate?: string | null) =>
            dataSet === 'PRODUCTION_COPY'
                ? translate('app-settings:DATA_SET_PRODUCTION_COPY_WITH_DATE', {
                      date: formatDate(snapshotDate, 'date') || '?',
                  })
                : translate('app-settings:DATA_SET_REGRESSION'),
        [translate],
    );

    // Only a RUNNING -> finished transition seen in this session gives a
    // message; opening the page on an old result does not.
    const previousState = useRef(status?.state);
    useEffect(() => {
        const wasRunning = previousState.current === 'RUNNING';
        previousState.current = status?.state;
        if (!wasRunning || !status || status.state === 'RUNNING') return;

        if (status.state === 'DONE') {
            Promise.all([
                client.query<{ denthubSettings: DenthubSetting }>({
                    query: getDenthubSettingsTemplate,
                    fetchPolicy: 'network-only',
                }),
                refetchDataSets(),
            ])
                .then(([{ data }]) => {
                    setAppSettings(data.denthubSettings);
                    setAppToasterContent({
                        isOpen: true,
                        severity: 'success',
                        message: translate('app-settings:DATA_SET_SWITCH_DONE', {
                            dataSet: dataSetLabel(status.dataSet, data.denthubSettings.activeDataSetSnapshotDate),
                        }),
                    });
                })
                .catch(() => undefined);
            return;
        }

        setAppToasterContent({
            isOpen: true,
            severity: 'error',
            message:
                status.state === 'INTERRUPTED'
                    ? translate('app-settings:DATA_SET_SWITCH_INTERRUPTED')
                    : translate('app-settings:DATA_SET_SWITCH_FAILED', { error: status.error ?? '' }),
        });
    }, [status, client, refetchDataSets, setAppSettings, setAppToasterContent, translate, dataSetLabel]);

    const [switchDataSet, { loading: starting }] = useMutation(switchDataSetTemplate, {
        onCompleted: () => {
            // The mutation's result is RUNNING; refetching through the status
            // query (rather than writing the cache) keeps one source of truth.
            refetchStatus().catch(() => undefined);
        },
        onError: (error) => setAppToasterContent({ isOpen: true, severity: 'error', message: error.message }),
    });

    const startSwitch = useCallback(
        (dataSet: DataSet) => {
            switchDataSet({ variables: { dataSet } });
        },
        [switchDataSet],
    );

    const value = useMemo(
        () => ({
            status,
            dataSets: dataSetsData?.dataSets ?? [],
            isRunning,
            starting,
            startSwitch,
        }),
        [status, dataSetsData, isRunning, starting, startSwitch],
    );

    return <DataSetSwitchContext.Provider value={value}>{children}</DataSetSwitchContext.Provider>;
};

export const useDataSetSwitch = () => useContext(DataSetSwitchContext);
